import { IconCheckFill } from "@/assets/icons/geist/IconCheckFill";
import { IconClock } from "@/assets/icons/geist/IconClock";
import { IconError } from "@/assets/icons/geist/IconError";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { AgentExecution_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { useMemo, type FC } from "react";

export const AgentExecutionStatusPill: FC<{ agentExecution: PlainAgentExecution }> = ({ agentExecution }) => {
    const { label, color, icon, tooltip } = useMemo<{
        label: string | null;
        color: string | null;
        icon: React.ReactNode | null;
        tooltip: string | null;
    }>(() => {
        if (!agentExecution.status?.phase) {
            return { label: null, color: null, icon: null, tooltip: null };
        }

        if (agentExecution?.spec?.desiredPhase === AgentExecution_Phase.STOPPED) {
            if (
                agentExecution.status?.phase === AgentExecution_Phase.WAITING_FOR_INPUT ||
                agentExecution.status?.phase === AgentExecution_Phase.STOPPED
            ) {
                return {
                    label: "Stopped",
                    color: "bg-[rgba(144,144,129,0.1)] pl-2",
                    icon: null,
                    tooltip: null,
                };
            }
            return {
                label: "Stopping",
                color: "bg-[rgba(144,144,129,0.1)]",
                icon: <IconSpinner size="sm" className="size-3.5 shrink-0 animate-spin text-content-tertiary" />,
                tooltip: null,
            };
        }

        switch (agentExecution.status.phase) {
            case AgentExecution_Phase.RUNNING:
                return {
                    label: "Working",
                    color: "bg-[rgba(144,144,129,0.1)]",
                    icon: <IconSpinner size="sm" className="size-3.5 shrink-0 animate-spin text-content-tertiary" />,
                    tooltip: null,
                };
            case AgentExecution_Phase.WAITING_FOR_INPUT:
                return {
                    label: "Waiting for input",
                    color: "bg-surface-orange",
                    icon: <IconClock size="sm" className="size-3.5 shrink-0 text-content-orange" />,
                    tooltip: null,
                };
            case AgentExecution_Phase.STOPPED:
                return agentExecution.status.failureMessage
                    ? {
                          label: "Failed",
                          color: "bg-surface-red",
                          icon: <IconError size="sm" className="size-3.5 shrink-0 text-content-red" />,
                          tooltip: agentExecution.status.failureMessage,
                      }
                    : {
                          label: "Ready for review",
                          color: "bg-surface-green",
                          icon: <IconCheckFill size="sm" className="size-3.5 shrink-0 text-content-green" />,
                          tooltip: null,
                      };
            default:
                return { label: null, color: null, icon: null, tooltip: null };
        }
    }, [agentExecution?.spec?.desiredPhase, agentExecution.status?.failureMessage, agentExecution.status?.phase]);

    if (!label) {
        return null;
    }

    return (
        <Tooltip content={tooltip || undefined}>
            <div
                className={cn(
                    "flex items-center gap-1 rounded-[32px] border-0.5 border-border-base py-0.5 pl-1.5 pr-2",
                    color,
                )}
            >
                {icon}
                <Text className="truncate text-sm font-bold">{label}</Text>
            </div>
        </Tooltip>
    );
};
